/**
 * Central route metadata for labels, breadcrumbs groups, and availability hints.
 */

export const ROUTE_META = {
  dashboard: {
    page: 'dashboard',
    title: 'Dashboard',
    label: 'Dashboard',
    group: 'plataforma',
    path: '/dashboard',
  },
  lojas: {
    page: 'lojas',
    title: 'Lojas',
    label: 'Lojas',
    group: 'plataforma',
    path: '/lojas',
  },
  'nova-loja': {
    page: 'nova-loja',
    title: 'Nova Loja',
    label: 'Nova Loja',
    group: 'plataforma',
    parent: 'lojas',
    path: '/lojas/nova',
  },
  loja: {
    page: 'loja',
    title: 'Detalhe da loja',
    label: 'Loja',
    group: 'plataforma',
    parent: 'lojas',
    path: '/lojas/:tenantId',
  },
  cockpit: {
    page: 'cockpit',
    title: 'Tenant Cockpit',
    label: 'Tenant Cockpit',
    group: 'plataforma',
    parent: 'loja',
    path: '/lojas/:tenantId/cockpit',
  },
  planos: {
    page: 'planos',
    title: 'Planos & Assinaturas',
    label: 'Planos & Assinaturas',
    group: 'plataforma',
    path: '/planos',
    future: true,
  },
  'planos-permissoes': {
    page: 'planos-permissoes',
    title: 'Planos e Permissões',
    label: 'Planos e Permissões',
    group: 'sistema',
    path: '/planos/permissoes',
  },
  inadimplencia: {
    page: 'inadimplencia',
    title: 'Inadimplência',
    label: 'Inadimplência',
    group: 'plataforma',
    path: '/inadimplencia',
  },
  billing: {
    page: 'billing',
    title: 'Billing & Inadimplência',
    label: 'Billing',
    group: 'plataforma',
    path: '/billing',
  },
  equipe: {
    page: 'equipe',
    title: 'Equipe e Permissões',
    label: 'Equipe e Permissões',
    group: 'sistema',
    path: '/equipe',
  },
  perfis: {
    page: 'perfis',
    title: 'Perfis de acesso',
    label: 'Perfis de acesso',
    group: 'sistema',
    parent: 'equipe',
    path: '/equipe/perfis',
    mockOnly: true,
  },
  config: {
    page: 'config',
    title: 'Configurações',
    label: 'Configurações',
    group: 'plataforma',
    path: '/configuracoes',
    mockOnly: true,
  },
  auditoria: {
    page: 'auditoria',
    title: 'Auditoria',
    label: 'Auditoria',
    group: 'sistema',
    path: '/auditoria',
  },
  integrations: {
    page: 'integrations',
    title: 'Integrations Center',
    label: 'Integrations Center',
    group: 'operacoes',
    path: '/integracoes',
    feature: 'FEATURE_INTEGRATIONS',
    future: true,
  },
  observabilidade: {
    page: 'observabilidade',
    title: 'Observabilidade',
    label: 'Observabilidade',
    group: 'operacoes',
    path: '/observabilidade',
    feature: 'FEATURE_OBSERVABILITY',
    future: true,
  },
  repairs: {
    page: 'repairs',
    title: 'Repairs & Diagnóstico',
    label: 'Repairs & Diagnóstico',
    group: 'operacoes',
    path: '/repairs',
    feature: 'FEATURE_REPAIRS_MODULE',
    future: true,
  },
  tickets: {
    page: 'tickets',
    title: 'Tickets',
    label: 'Tickets',
    group: 'suporte',
    path: '/suporte/tickets',
    mvpHidden: true,
  },
  'knowledge-base': {
    page: 'knowledge-base',
    title: 'Base de Conhecimento',
    label: 'Base de Conhecimento',
    group: 'suporte',
    path: '/suporte/base-conhecimento',
    mvpHidden: true,
  },
  'support-reports': {
    page: 'support-reports',
    title: 'Relatórios de Suporte',
    label: 'Relatórios de Suporte',
    group: 'suporte',
    path: '/suporte/relatorios',
    mvpHidden: true,
  },
  'notificacoes-ops': {
    page: 'notificacoes-ops',
    title: 'Notificações Operacionais',
    label: 'Notificações',
    group: 'plataforma',
    path: '/notificacoes',
    future: true,
  },
  'tech-spec': {
    page: 'tech-spec',
    title: 'Especificação Técnica',
    label: 'Spec Técnica',
    group: 'sistema',
    path: '/sistema/especificacao-tecnica',
    future: true,
  },
};

import { MODULE_TAB_CONFIG, resolveTabFromSegment } from './module-tabs.js';

export function getRouteMeta(page) {
  return ROUTE_META[page] || null;
}

export function getPageLabel(page) {
  return ROUTE_META[page]?.title || ROUTE_META[page]?.label || 'Tezus';
}

/**
 * Build breadcrumb trail for the current URL (page + optional tab/entity).
 * @returns {{ label: string, page?: string, to?: string, store?: boolean, current?: boolean }[]}
 */
export function getBreadcrumbTrail({ page, tabSegment = null, store = null, tenantId = null, eventId = null } = {}) {
  const meta = getRouteMeta(page);
  const trail = [];
  const tid = tenantId || store?.id || store?.tenant_id || null;

  if (page === 'cockpit' && (store || tid)) {
    trail.push({ label: 'Lojas', page: 'lojas', to: '/lojas' });
    trail.push({
      label: store?.name || 'Loja',
      page: 'loja',
      store: true,
      to: tid ? `/lojas/${encodeURIComponent(tid)}` : '/lojas',
    });
    trail.push({ label: 'Cockpit', current: !tabSegment, to: tid ? `/lojas/${encodeURIComponent(tid)}/cockpit` : undefined });
    if (tabSegment) {
      const resolved = resolveTabFromSegment('cockpit', tabSegment);
      const tab = MODULE_TAB_CONFIG.cockpit.tabs.find((t) => t.id === resolved.tabId);
      if (tab && tab.segment) trail.push({ label: tab.label || tab.id, current: true });
    }
    return trail;
  }

  if (page === 'loja' && (store || tid)) {
    trail.push({ label: 'Lojas', page: 'lojas', to: '/lojas' });
    trail.push({ label: store?.name || 'Loja', current: true });
    return trail;
  }

  if (page === 'tickets' || page === 'knowledge-base' || page === 'support-reports') {
    trail.push({ label: 'Suporte' });
    trail.push({ label: meta?.title || page, current: !tabSegment, to: meta?.path });
  } else if (page === 'equipe' || page === 'usuarios' || page === 'perfis') {
    trail.push({ label: 'Sistema' });
    trail.push({ label: meta?.title || 'Equipe', current: true });
    return trail;
  } else {
    trail.push({ label: meta?.title || getPageLabel(page), current: !tabSegment && !eventId, to: meta?.path });
  }

  const moduleKey = Object.keys(MODULE_TAB_CONFIG).find((k) => MODULE_TAB_CONFIG[k].page === page);
  if (moduleKey && tabSegment) {
    const resolved = resolveTabFromSegment(moduleKey, tabSegment);
    const tab = MODULE_TAB_CONFIG[moduleKey].tabs.find((t) => t.id === resolved.tabId);
    if (tab && tab.segment) {
      if (trail.length) trail[trail.length - 1].current = false;
      trail.push({ label: tab.label || tab.id, current: true });
    }
  }

  if (page === 'auditoria' && eventId) {
    if (trail.length) trail[trail.length - 1].current = false;
    trail.push({ label: `Evento #${eventId}`, current: true });
  }

  return trail;
}
